// Simplified redaction functionality for HumanBehavior SDK
// Since rrweb auto-redacts all input fields by default, this module only handles
// selectively unredacting specific fields (except passwords which remain protected)

import { logDebug, logWarn } from './utils/logger';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Credential-bearing URL parameter names, dropped from every captured URL.
 *
 * Captured URLs previously went through verbatim, query string and hash
 * included, into session rows, spans, logs and network events. That means a
 * password-reset link, a magic link, an OAuth `?code=`, an implicit-flow
 * `#access_token=`, or a pre-signed S3 URL landed in our store as a live
 * credential, and a replay viewer could read it.
 */
const SENSITIVE_URL_PARAM_NAMES = new Set([
    'access_token',
    'id_token',
    'refresh_token',
    'token',
    'code',
    'auth',
    'authorization',
    'session',
    'sid',
    'key',
    'secret',
    'password',
    'passwd',
    'pwd',
    'otp',
    'signature',
    'sig',
    'state',
    'invite',
]);

/** Substrings that make a parameter sensitive regardless of exact spelling. */
const SENSITIVE_URL_PARAM_PATTERNS = [
    'token',
    'secret',
    'password',
    'passwd',
    'signature',
    'apikey',
    'api_key',
    'auth',
];

const URL_REDACTED = 'REDACTED';

function isSensitiveParamName(name: string): boolean {
    const lower = name.toLowerCase();
    if (SENSITIVE_URL_PARAM_NAMES.has(lower)) return true;
    return SENSITIVE_URL_PARAM_PATTERNS.some((p) => lower.includes(p));
}

function redactParams(search: URLSearchParams): boolean {
    let changed = false;
    for (const name of Array.from(search.keys())) {
        if (!isSensitiveParamName(name)) continue;
        search.set(name, URL_REDACTED);
        changed = true;
    }
    return changed;
}

/**
 * Replace credential-bearing query and hash parameters with `REDACTED`.
 *
 * Returns the input untouched when nothing matched, so a URL that carries no
 * secrets is never reformatted by `URL` normalization.
 */
export function sanitizeUrl(raw: string | null | undefined): string {
    if (!raw) return raw ?? '';
    try {
        const base = isBrowser ? window.location.href : 'http://localhost/';
        const url = new URL(raw, base);
        let changed = redactParams(url.searchParams);

        // Implicit-flow tokens arrive in the fragment, formatted like a query.
        const fragment = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
        if (fragment && /[=&]/.test(fragment)) {
            const hashParams = new URLSearchParams(fragment);
            if (redactParams(hashParams)) {
                url.hash = `#${hashParams.toString()}`;
                changed = true;
            }
        }

        if (!changed) return raw;
        // Relative inputs must stay relative.
        return /^[a-z][a-z0-9+.-]*:|^\/\//i.test(raw)
            ? url.toString()
            : `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return raw;
    }
}

export interface RedactionOptions {
    redactedText?: string;
    excludeSelectors?: string[];
    userFields?: string[]; // Fields that the user wants to unredact (legacy)
    redactionStrategy?: {
        mode: 'privacy-first' | 'visibility-first';
        unredactFields?: string[]; // Fields to make visible (when mode: 'privacy-first')
        redactFields?: string[];   // Fields to hide (when mode: 'visibility-first')
    };
    legacyRedactFields?: string[]; // For backward compatibility
}

export class RedactionManager {
    private redactedText: string = '[REDACTED]';
    private unredactedFields: Set<string> = new Set(); // Fields that user wants to unredact
    private redactedFields: Set<string> = new Set(); // Fields that user wants to redact
    private redactionMode: 'privacy-first' | 'visibility-first' = 'privacy-first';
    private excludeSelectors: string[] = [
        '[data-no-redact="true"]',
        '.human-behavior-no-redact'
    ];

    constructor(options?: RedactionOptions) {
        if (options?.redactedText) {
            this.redactedText = options.redactedText;
        }
        if (options?.excludeSelectors) {
            this.excludeSelectors = [...this.excludeSelectors, ...options.excludeSelectors];
        }
        
        // Handle new redaction strategy
        if (options?.redactionStrategy) {
            this.redactionMode = options.redactionStrategy.mode;

            if (this.redactionMode === 'privacy-first') {
                // Privacy-first: everything redacted by default, unredact specific fields
                if (options.redactionStrategy.unredactFields) {
                    this.setFieldsToUnredact(options.redactionStrategy.unredactFields);
                }
            } else {
                // Visibility-first: everything visible by default, redact specific fields
                // Default to only redacting passwords if no specific fields provided
                // Support zero-config authoring: allow data-hb-redact="true" marks
                const defaultMarks = ['input[type="password"]', '[data-hb-redact="true"]'];
                const fieldsToRedact = options.redactionStrategy.redactFields && options.redactionStrategy.redactFields.length > 0
                    ? options.redactionStrategy.redactFields
                    : defaultMarks;
                this.setFieldsToRedact(fieldsToRedact);
            }
        }
        
        // Handle legacy redactFields (backward compatibility)
        if (options?.legacyRedactFields) {
            this.setFieldsToUnredact(options.legacyRedactFields);
        }
        
        // Handle legacy userFields
        if (options?.userFields) {
            this.setFieldsToUnredact(options.userFields);
        }
    }

    /**
     * Set specific fields to be redacted (for visibility-first mode)
     * @param fields Array of CSS selectors for fields to redact
     */
    public setFieldsToRedact(fields: string[]): void {
        this.redactedFields.clear();
        
        // Always include password fields in redacted list
        const passwordFields = [
            'input[type="password"]',
            'input[type="password" i]',
            '[type="password"]',
            '[type="password" i]'
        ];
        
        // Add password fields and user-specified fields
        [...passwordFields, ...fields].forEach(field => {
            this.redactedFields.add(field);
        });
        
        if (this.redactedFields.size > 0) {
            logDebug(`Redaction: Active for ${this.redactedFields.size} field(s):`, Array.from(this.redactedFields));
        } else {
            logDebug('Redaction: No fields to redact');
        }
        
        this.applyRedactionClasses();
    }

    /**
     * Set specific fields to be unredacted (everything else stays redacted by rrweb)
     * @param fields Array of CSS selectors for fields to unredact
     */
    public setFieldsToUnredact(fields: string[]): void {
        this.unredactedFields.clear();
        
        // Filter out password fields (they cannot be unredacted)
        const validFields = fields.filter(field => {
            const isPasswordField = this.isPasswordSelector(field);
            if (isPasswordField) {
                logWarn(`Cannot unredact password field: ${field} - Password fields are always protected`);
                return false;
            }
            return true;
        });
        
        validFields.forEach(field => this.unredactedFields.add(field));
        
        if (validFields.length > 0) {
            logDebug(`Unredaction: Active for ${validFields.length} field(s):`, validFields);
        } else {
            logDebug('Unredaction: No valid fields to unredact');
        }
        
        this.applyUnredactionClasses();
    }

    /**
     * Remove specific fields from unredaction (they become redacted again)
     * @param fields Array of CSS selectors for fields to redact
     */
    public redactFields(fields: string[]): void {
        fields.forEach(field => {
            this.unredactedFields.delete(field);
        });
        
        if (this.unredactedFields.size > 0) {
            logDebug(`Unredaction: Removed ${fields.length} field(s), ${this.unredactedFields.size} remaining:`, Array.from(this.unredactedFields));
        } else {
            logDebug('Unredaction: All fields redacted');
        }
        
        this.applyUnredactionClasses();
    }

    /**
     * Clear all unredacted fields (everything becomes redacted again)
     */
    public clearUnredactedFields(): void {
        this.unredactedFields.clear();
        logDebug('Unredaction: All fields cleared, everything redacted');
        
        this.removeUnredactionClasses();
    }

    /**
     * Check if any fields are currently unredacted
     */
    public hasUnredactedFields(): boolean {
        return this.unredactedFields.size > 0;
    }

    /**
     * Get the current redaction mode
     */
    public getRedactionMode(): 'privacy-first' | 'visibility-first' {
        return this.redactionMode;
    }

    /**
     * Get the currently unredacted fields
     */
    public getUnredactedFields(): string[] {
        return Array.from(this.unredactedFields);
    }

    /**
     * Get CSS selectors for rrweb masking configuration
     * Returns null if no fields are unredacted (everything stays redacted)
     */
    public getMaskTextSelector(): string | null {
        if (this.redactionMode === 'privacy-first') {
            // Privacy-first: mask everything except unredacted fields
            if (this.unredactedFields.size === 0) {
                return null; // Everything stays redacted
            }
            return Array.from(this.unredactedFields).join(',');
        } else {
            // Visibility-first: mask only redacted fields
            if (this.redactedFields.size === 0) {
                return null; // Nothing to redact
            }
            return Array.from(this.redactedFields).join(',');
        }
    }

    /**
     * Apply redaction classes to DOM elements (for visibility-first mode)
     * Adds 'rr-mask' class to elements that should be redacted
     */
    public applyRedactionClasses(): void {
        if (this.redactedFields.size === 0) {
            return;
        }

        // Check if DOM is ready
        if (typeof document === 'undefined' || document.readyState === 'loading') {
            logDebug('DOM not ready, deferring redaction class application');
            return;
        }

        // Add 'rr-mask' class to redacted elements
        this.redactedFields.forEach(selector => {
            try {
                const elements = document.querySelectorAll(selector);
                elements.forEach(element => {
                    if (element && element.classList) {
                        element.classList.add('rr-mask');
                    }
                });
                logDebug(`Added rr-mask class to ${elements.length} element(s) for selector: ${selector}`);
            } catch (e) {
                logWarn(`Invalid selector: ${selector}`);
            }
        });
    }

    /**
     * Apply unredaction classes to DOM elements
     * Removes 'rr-mask' class from elements that should be unredacted
     */
    public applyUnredactionClasses(): void {
        if (this.unredactedFields.size === 0) {
            return;
        }

        // Check if DOM is ready
        if (typeof document === 'undefined' || document.readyState === 'loading') {
            logDebug('DOM not ready, deferring unredaction class application');
            return;
        }

        // Remove 'rr-mask' class from unredacted elements
        this.unredactedFields.forEach(selector => {
            try {
                const elements = document.querySelectorAll(selector);
                elements.forEach(element => {
                    if (element && element.classList) {
                        element.classList.remove('rr-mask');
                    }
                });
                logDebug(`Removed rr-mask class from ${elements.length} element(s) for selector: ${selector}`);
            } catch (e) {
                logWarn(`Invalid selector: ${selector}`);
            }
        });
    }

    /**
     * Remove all unredaction classes from DOM elements
     */
    public removeUnredactionClasses(): void {
        // Note: This doesn't add 'rr-mask' classes back - rrweb handles that automatically
        logDebug('Unredaction classes removed');
    }

    /**
     * Check if a selector represents a password field
     */
    private isPasswordSelector(selector: string): boolean {
        const passwordPatterns = [
            'input[type="password"]',
            'input[type="password" i]',
            '[type="password"]',
            '[type="password" i]'
        ];
        
        return passwordPatterns.some(pattern => 
            selector.toLowerCase().includes(pattern.toLowerCase().replace(/[\[\]]/g, ''))
        );
    }

    /**
     * Get the original value of an element (for debugging)
     */
    public getOriginalValue(element: HTMLElement): string | undefined {
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            return element.value;
        }
        return undefined;
    }

    /**
     * Check if an element is currently unredacted
     */
    public isElementUnredacted(element: HTMLElement): boolean {
        return this.shouldUnredactElement(element);
    }

    /**
     * Check if an element should be unredacted
     */
    public shouldUnredactElement(element: HTMLElement): boolean {
        // Fail closed: any unexpected error masks the element rather than
        // leaking it. "Unredact" is the privileged decision, so when in doubt
        // we say no.
        try {
            if (this.redactionMode === 'privacy-first') {
                // Privacy-first: only elements that positively match an
                // allowlisted selector are shown; everything else stays masked.
                if (this.unredactedFields.size === 0) {
                    return false; // Nothing unredacted
                }

                for (const selector of this.unredactedFields) {
                    try {
                        if (element.matches(selector)) {
                            return true;
                        }
                    } catch (e) {
                        // Can't evaluate this allowlist selector — do NOT unredact on it.
                        logWarn(`Invalid selector: ${selector}`);
                    }
                }
                return false;
            } else {
                // Visibility-first: elements are shown unless they match a
                // redacted selector.
                if (this.redactedFields.size === 0) {
                    return true; // Nothing redacted, everything visible
                }

                for (const selector of this.redactedFields) {
                    try {
                        if (element.matches(selector)) {
                            return false; // Element is redacted
                        }
                    } catch (e) {
                        // Can't evaluate a redact selector — fail closed and mask
                        // this element rather than risk exposing a field that was
                        // meant to be hidden.
                        logWarn(`Invalid selector: ${selector}`);
                        return false;
                    }
                }
                return true; // Element is not redacted
            }
        } catch {
            return false;
        }
    }
}

// Export a default instance
export const redactionManager = new RedactionManager();

// Export the class for custom instances
export default RedactionManager; 